import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import Layout from '../Components/Layout/Layout';
import StyledSwitch from "../Components/Layout/StyledSwitch";
import { Card, CardContent } from "@/Components/ui/card";
import { Input } from "@/Components/ui/input";
import axiosInstance from '../utils/axiosConfig';
import QRCodeStyling from "qr-code-styling";
import { Plus, ImageDown, ExternalLink, RefreshCw } from "lucide-react";

const qrCodeX = new QRCodeStyling({
    width: 320,
    height: 320,
    dotsOptions: {
        color: "#6a1a4c",
        type: "extra-rounded"
    },
    imageOptions: {
        crossOrigin: "anonymous",
        margin: 20
    }
});

const QRCodePage = () => {
    const [isQRMounted, setIsQRMounted] = useState(false);
    const [url, setUrl] = useState('');
    const [options, setOptions] = useState({
        id: null,
        name: '',
        qrcode: null,
        ispublished: true,
        price: true,
        diets: true,
        allergens: true,
        translate: true,
        logo: '',
        backgroundcolor: '#ffffff',
        textcolor: '#000000',
        font: 'Roboto'
    });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (ref.current && !isQRMounted) {
            try {
                // Wacht tot de ref gemount is
                setTimeout(() => {
                    qrCodeX.append(ref.current);
                    setIsQRMounted(true);
                }, 100);
            } catch (error) {
                console.error('Error mounting QR:', error);
            }
        }

        // Cleanup
        return () => {
            if (ref.current) {
                while (ref.current.firstChild) {
                    ref.current.firstChild.remove();
                }
            }
            setIsQRMounted(false);
        };
    }, [ref.current]); // Only run when ref changes

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await axiosInstance.get('/menu/GetQrSettings');
                setOptions(response.data);

                const newUrl = `${import.meta.env.VITE_APPURL}show/${response.data.qrcode}`;
                setUrl(newUrl);

                // Alleen updaten als QR al gemount is
                if (isQRMounted && ref.current) {
                    console.log('Updating QR with URL:', newUrl); // Debug log
                    qrCodeX.update({
                        data: newUrl,
                        width: 320,
                        height: 320,
                    });
                }
            } catch (error) {
                console.error('Error fetching QR settings:', error);
                setError('Er is een fout opgetreden bij het ophalen van de instellingen.');
            }
        };

        fetchSettings();
    }, [isQRMounted]); // Run when QR mounting status changes

    // Handmatige refresh functie
    const handleRefreshQR = async () => {
        setIsRefreshing(true);
        try {
            // Clear bestaande QR
            if (ref.current) {
                while (ref.current.firstChild) {
                    ref.current.firstChild.remove();
                }
            }

            // Wacht even en mount opnieuw
            setTimeout(() => {
                if (ref.current && url) {
                    qrCodeX.update({
                        data: url,
                        width: 320,
                        height: 320,
                    });
                    qrCodeX.append(ref.current);
                }
                setIsRefreshing(false);
            }, 200);
        } catch (error) {
            console.error('Error refreshing QR:', error);
            setIsRefreshing(false);
        }
    };

    const onDownloadClick = () => {
        qrCodeX.download({
            extension: "png"
        });
    };

    const onOpenClick = () => {
        window.open(url, '_blank');
    };

    // Google Fonts opties
    const googleFonts = [
        { name: 'Roboto', category: 'Sans-serif', style: "'Roboto', sans-serif" },
        { name: 'Open Sans', category: 'Sans-serif', style: "'Open Sans', sans-serif" },
        { name: 'Lato', category: 'Sans-serif', style: "'Lato', sans-serif" },
        { name: 'Montserrat', category: 'Sans-serif', style: "'Montserrat', sans-serif" },
        { name: 'Poppins', category: 'Sans-serif', style: "'Poppins', sans-serif" },
        { name: 'Playfair Display', category: 'Serif', style: "'Playfair Display', serif" },
        { name: 'Merriweather', category: 'Serif', style: "'Merriweather', serif" },
        { name: 'PT Serif', category: 'Serif', style: "'PT Serif', serif" },
        { name: 'Ubuntu', category: 'Sans-serif', style: "'Ubuntu', sans-serif" },
        { name: 'Noto Sans', category: 'Sans-serif', style: "'Noto Sans', sans-serif" },
        { name: 'Source Sans Pro', category: 'Sans-serif', style: "'Source Sans Pro', sans-serif" },
        { name: 'Dancing Script', category: 'Handwriting', style: "'Dancing Script', cursive" },
        { name: 'Pacifico', category: 'Handwriting', style: "'Pacifico', cursive" },
        { name: 'Quicksand', category: 'Sans-serif', style: "'Quicksand', sans-serif" },
        { name: 'Oswald', category: 'Sans-serif', style: "'Oswald', sans-serif" }
    ];

    // Groepeer fonts per categorie
    const fontCategories = {
        'Sans-serif': googleFonts.filter(font => font.category === 'Sans-serif'),
        'Serif': googleFonts.filter(font => font.category === 'Serif'),
        'Handwriting': googleFonts.filter(font => font.category === 'Handwriting'),
    };

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await axiosInstance.get('/menu/GetQrSettings');
                setOptions(response.data);

                qrCodeX.append(ref.current);
                setUrl(`${import.meta.env.VITE_APPURL}show/` + response.data.qrcode);
                qrCodeX.update({
                    data: `${import.meta.env.VITE_APPURL}show/` + response.data.qrcode
                });
            } catch (error) {
                console.error('Error fetching QR settings:', error);
                setError('Er is een fout opgetreden bij het ophalen van de instellingen.');
            }
        };

        fetchSettings();
    }, []);

    const updateSettings = async (updatedOptions) => {
        setIsSaving(true);
        setError(null);

        try {
            await axiosInstance.post('/menu/SetQrSettings', {
                id: updatedOptions.id,
                name: updatedOptions.name,
                qrcode: updatedOptions.qrcode,
                ispublished: updatedOptions.ispublished,
                price: updatedOptions.price,
                diets: updatedOptions.diets,
                allergens: updatedOptions.allergens,
                translate: updatedOptions.translate,
                logo: updatedOptions.logo,
                backgroundcolor: updatedOptions.backgroundcolor,
                textcolor: updatedOptions.textcolor,
                font: updatedOptions.font
            });
        } catch (error) {
            console.error('Error updating QR settings:', error);
            setError('Er is een fout opgetreden bij het opslaan van de instellingen.');
            return false;
        } finally {
            setIsSaving(false);
        }
        return true;
    };

    const handleSwitchChange = async (checked, settingKey) => {
        const updatedOptions = {
            ...options,
            [settingKey]: checked
        };
        setOptions(updatedOptions);

        const success = await updateSettings(updatedOptions);
        if (!success) {
            setOptions(prevState => ({
                ...prevState,
                [settingKey]: !checked
            }));
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleStyleChange = async (name, value) => {
        const updatedOptions = {
            ...options,
            [name]: value
        };
        setOptions(updatedOptions);
        await updateSettings(updatedOptions);
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const formData = new FormData();
            formData.append('logo', file);

            try {
                setIsSaving(true);
                const response = await axiosInstance.post('/menu/UploadLogo', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                if (response.data.logoUrl) {
                    await handleStyleChange('logo', response.data.logoUrl);
                }
            } catch (error) {
                console.error('Error uploading logo:', error);
                setError('Er is een fout opgetreden bij het uploaden van het logo.');
            } finally {
                setIsSaving(false);
            }
        }
    };

    const settings = [
        {
            id: 'publish-ispublished',
            title: 'Publiceren',
            key: 'ispublished',
            description: 'Maak de QR-code openbaar beschikbaar'
        },
        {
            id: 'publish-price',
            title: 'Toon prijzen',
            key: 'price',
            description: 'Toon prijzen bij de gerechten'
        },
        {
            id: 'publish-diets',
            title: 'Toon diëten',
            key: 'diets',
            description: 'Toon dieetvoorkeuren bij de gerechten'
        },
        {
            id: 'publish-allergens',
            title: 'Toon allergenen',
            key: 'allergens',
            description: 'Toon allergenen bij de gerechten'
        },
        {
            id: 'publish-translate',
            title: 'Toon vertalingen',
            key: 'translate',
            description: 'Toon google translate op pagina'
        }
    ];

    useEffect(() => {
        // Laad de Google Fonts
        const loadGoogleFonts = () => {
            const link = document.createElement('link');
            link.href = 'https://fonts.googleapis.com/css2?family=' +
                googleFonts.map(font => font.name.replace(' ', '+')).join('&family=');
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        };

        loadGoogleFonts();
        // ... rest van bestaande useEffect logica ...
    }, []);

    return (
        <Layout>
            <div className="relative w-full max-w-[1100px] mx-auto px-4 md:px-6">
                <h2 className="text-xl font-normal font-inter text-ps-blue-dark mb-6">
                    {options.name}
                </h2>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex flex-col md:flex-row gap-4 md:gap-8">
                    {/* QR Code preview - Full width on mobile, 1/3 on desktop */}
                    <div className="w-full md:w-1/3">
                        <Card>
                            <CardContent className="p-4 md:p-6">
                                {options.qrcode ? (
                                    <>
                                        <div className="aspect-square w-full rounded-md flex items-center justify-center bg-white">
                                            <div
                                                ref={ref}
                                                className="max-w-full w-[320px] h-[320px] mx-auto"
                                                style={{ minHeight: '320px' }}
                                            />
                                        </div>
                                        <div className="mt-4 md:mt-8 flex justify-center gap-2">
                                            <button
                                                onClick={onDownloadClick}
                                                className="flex items-center gap-2 px-4 py-2 min-w-32 bg-ps-blue-dark text-white rounded-md hover:bg-ps-blue-darker"
                                            >
                                                <ImageDown className="w-5 h-5" />
                                                Download
                                            </button>

                                            <button
                                                onClick={onOpenClick}
                                                className="flex items-center gap-2 px-4 py-2 min-w-32 bg-ps-blue-dark text-white rounded-md hover:bg-ps-blue-darker"
                                            >
                                                <ExternalLink className="w-5 h-5" />
                                                Open
                                            </button>

                                            <button
                                                onClick={handleRefreshQR}
                                                disabled={isRefreshing}
                                                className="flex items-center justify-center w-10 h-10 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
                                                title="QR-code opnieuw laden"
                                            >
                                                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <div className="aspect-square w-full border rounded-md flex items-center justify-center bg-gray-50 text-gray-400">
                                        QR Code niet beschikbaar
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Settings - Full width on mobile, 2/3 on desktop */}
                    <div className="w-full md:flex-1">
                        <Card>
                            <CardContent className="p-4 md:p-6">
                                <div className="space-y-4 md:space-y-6">
                                    {settings.map(setting => (
                                        <div
                                            key={setting.id}
                                            className="flex flex-col sm:flex-row sm:items-center justify-between border-b last:border-b-0 pb-4 last:pb-0 gap-2 sm:gap-4"
                                        >
                                            <div>
                                                <h3 className="font-inter text-ps-blue-dark">
                                                    {setting.title}
                                                </h3>
                                                <p className="text-sm text-gray-500">
                                                    {setting.description}
                                                </p>
                                            </div>
                                            <div className="self-start sm:self-center">
                                                <StyledSwitch
                                                    title={setting.title}
                                                    checked={options[setting.key]}
                                                    onCheckedChange={(checked) => handleSwitchChange(checked, setting.key)}
                                                    id={setting.id}
                                                    disabled={isSaving}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {/* Styling options - Full width */}
                <div className="w-full mt-4 md:mt-6">
                    <Card>
                        <CardContent className="p-4 md:p-6">
                            <h3 className="text-lg font-inter text-ps-blue-dark mb-4">
                                Styling opties
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                <div>
                                    <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        Logo
                                    </label>

                                    {/* Toon huidige logo als die bestaat */}
                                    {options.logo && (
                                        <div className="mb-3 p-3 bg-gray-50 rounded border">
                                            <div className="flex items-center gap-3">
                                                <img
                                                    src={options.logo}
                                                    alt="Huidige logo"
                                                    className="w-12 h-12 object-contain bg-white rounded border"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                                <div className="flex-1">
                                                    <p className="text-sm font-inter text-gray-700 mb-2">Huidige logo:</p>
                                                    <img
                                                        src={`${import.meta.env.VITE_APIURL}menu/GetImage/${options.qrcode}`}
                                                        className="max-h-32 max-w-64 w-auto"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <Input
                                        type="file"
                                        name="logo"
                                        onChange={handleFileUpload}
                                        className="w-full p-2 border rounded bg-white"
                                        accept="image/*"
                                        disabled={isSaving}
                                    />
                                    {!options.logo && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Geen logo geselecteerd
                                        </p>
                                    )}
                                </div>

                                {/* Colors */}
                                <div>
                                    <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        Kleuren
                                    </label>
                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-500 min-w-[80px]">Achtergrond:</span>
                                            <Input
                                                type="color"
                                                name="backgroundColor"
                                                value={options.backgroundcolor}
                                                onChange={(e) => handleStyleChange('backgroundcolor', e.target.value)}
                                                className="w-10 p-1 border rounded bg-white h-10"
                                                disabled={isSaving}
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-500 min-w-[80px]">Tekst:</span>
                                            <Input
                                                type="color"
                                                name="textColor"
                                                value={options.textcolor}
                                                onChange={(e) => handleStyleChange('textcolor', e.target.value)}
                                                className="w-10 p-1 border rounded bg-white h-10"
                                                disabled={isSaving}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Font selection */}
                                <div className="md:col-span-2 mb-2">
                                    <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        Lettertype
                                    </label>
                                    <select
                                        name="font"
                                        value={options.font}
                                        onChange={(e) => handleStyleChange('font', e.target.value)}
                                        className="w-full p-2 border rounded bg-white"
                                        disabled={isSaving}
                                    >
                                        {Object.entries(fontCategories).map(([category, fonts]) => (
                                            <optgroup key={category} label={category}>
                                                {fonts.map(font => (
                                                    <option
                                                        key={font.name}
                                                        value={font.name}
                                                        style={{ fontFamily: font.style }}
                                                    >
                                                        {font.name}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </Layout>
    );
};

export default QRCodePage;